import React, { useRef, useEffect, useState } from "react";
import Fuse from "fuse.js";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "./App.css";
import { counties } from "./counties";
import { supabase } from "./supabaseClient";
import toast, { Toaster } from "react-hot-toast";

export default function MapApp() {

  const [selectedCounty, setSelectedCounty] = useState("monmouth");
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const fuseRef = useRef(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const selectedMarkerRef = useRef(null);

  const [propertyList, setPropertyList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 2000000 });
  const [classFilter, setClassFilter] = useState("All");
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteInput, setNoteInput] = useState("");
  const [propertyToSave, setPropertyToSave] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);

  // Initialize map
  useEffect(() => {
    if (mapRef.current) return;
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: `https://api.maptiler.com/maps/hybrid/style.json?key=${process.env.REACT_APP_MAPTILER_KEY}`,
      center: [-74, 40],
      zoom: 8,
    });
    mapRef.current = map;
  }, []);

  // Load county GeoJSON and markers
  useEffect(() => {
    const county = counties[selectedCounty];
    if (!county) return;

    // Clear previous state when switching counties
    setPropertyList([]);
    setSelectedFeature(null);
    setSearchTerm("");
    setSuggestions([]);
    
    // Remove any existing markers and force map cleanup
    if (selectedMarkerRef.current) {
      selectedMarkerRef.current.remove();
      selectedMarkerRef.current = null;
    }
    document.querySelectorAll(".custom-marker-logo").forEach(el => el.remove());
    
    // Force cleanup of map layers before loading new data
    const map = mapRef.current;
    if (map && map.isStyleLoaded()) {
      try {
        if (map.getLayer("properties-layer")) map.removeLayer("properties-layer");
        if (map.getSource("county-source")) map.removeSource("county-source");
        if (map.getLayer("selected-marker-layer")) map.removeLayer("selected-marker-layer");
        if (map.getSource("selected-property")) map.removeSource("selected-property");
        map.triggerRepaint();
      } catch (e) {
        // Expected - layers may not exist yet
      }
    }

    fetch(county.geojsonPath)
      .then((r) => r.json())
      .then((geojson) => {
        const seen = new Set();
        const list = geojson.features
          .map((f) => {
            const rawProps = JSON.parse(JSON.stringify(f.properties));
            const p = Object.fromEntries(
              Object.entries(rawProps).map(([k, v]) => [k.trim().toLowerCase(), v])
            );

            const label = `${p["property location"] || "Unknown"}, ${p["city/state/zip"] || "Unknown"}`;
            if (!seen.has(label)) {
              seen.add(label);
              return {
                id: label,
                label,
                coords: f.geometry.coordinates,
                props: p,
              };
            }
            return null;
          })
          .filter(Boolean);
        setPropertyList(list);

        const map = mapRef.current;
        if (!map) return;

        const addDataToMap = () => {
          try {
            // Force complete cleanup of old layers and sources
            if (map.getLayer("properties-layer")) {
              map.removeLayer("properties-layer");
            }
            if (map.getSource("county-source")) {
              map.removeSource("county-source");
            }
            
            // Force map re-render to clear any cached visual elements
            map.triggerRepaint();

            map.addSource("county-source", {
              type: "geojson",
              data: geojson,
            });

            map.addLayer({
              id: "properties-layer",
              type: "circle",
              source: "county-source",
              paint: {
                "circle-radius": 5,
                "circle-color": [
                  "interpolate",
                  ["linear"],
                  ["get", "Sale Price"],
                  0, "#f1eef6",
                  200000, "#bdc9e1",
                  500000, "#74a9cf",
                  1000000, "#2b8cbe",
                  2000000, "#045a8d",
                ],
                "circle-stroke-width": 1,
                "circle-stroke-color": "#ffffff",
              },
            });

            if (map.getLayer("selected-marker-layer")) map.removeLayer("selected-marker-layer");
            if (map.getSource("selected-property")) map.removeSource("selected-property");

            map.addSource("selected-property", {
              type: "geojson",
              data: { type: "FeatureCollection", features: [] },
            });

            // Fit map to new data bounds
            if (geojson.features.length > 0) {
              const coordinates = geojson.features.map(f => f.geometry.coordinates);
              const bounds = coordinates.reduce((bounds, coord) => {
                return bounds.extend(coord);
              }, new maplibregl.LngLatBounds(coordinates[0], coordinates[0]));
              
              map.fitBounds(bounds, { padding: 50 });
            }

            // Remove previous click handlers and add new ones
            map.off("click", "properties-layer");
            map.on("click", "properties-layer", (e) => {
              const f = e.features[0];
              const rawProps = JSON.parse(JSON.stringify(f.properties));
              
              const p = Object.fromEntries(
                Object.entries(rawProps).map(([k, v]) => [k.trim().toLowerCase(), v])
              );
              
              const coords = f.geometry.coordinates;
              const label = `${p["property location"] || "Unknown"}, ${p["city/state/zip"] || "Unknown"}`;
              setSelectedFeature({ id: label, label, coords, props: p });

              map.flyTo({ center: coords, zoom: 17, speed: 0.8, curve: 1.2, easing: (t) => t });

              // Remove any previous logo markers
              document.querySelectorAll(".custom-marker-logo").forEach(el => el.remove());

              // Create and add your logo marker
              const el = document.createElement("div");
              el.className = "custom-marker-logo";
              el.style.backgroundImage = `url("/realmaplogo.png")`;
              el.style.width = "30px";
              el.style.height = "30px";
              el.style.backgroundSize = "contain";
              el.style.backgroundRepeat = "no-repeat";
              el.style.borderRadius = "50%";
              new maplibregl.Marker(el).setLngLat(coords).addTo(map);
            });

            map.on("mouseenter", "properties-layer", () => {
              map.getCanvas().style.cursor = "pointer";
            });

            map.on("mouseleave", "properties-layer", () => {
              map.getCanvas().style.cursor = "";
            });
            
          } catch (error) {
            console.error("Error adding data to map:", error);
          }
        };

        if (map.isStyleLoaded()) {
          addDataToMap();
        } else {
          map.once("styledata", addDataToMap);
        }
      })
      .catch((err) => {
        console.error("GeoJSON load error:", err);
      });
  }, [selectedCounty]);

  useEffect(() => {
    if (!propertyList.length) return;
    fuseRef.current = new Fuse(propertyList, {
      keys: ["label"],
      threshold: 0.3,
      ignoreLocation: true,
    });
  }, [propertyList]);

  useEffect(() => {
    if (!fuseRef.current || searchTerm.length < 2) {
      setSuggestions([]);
      return;
    }

    const results = fuseRef.current.search(searchTerm).map((r) => r.item);
    const filtered = results.filter((p) => {
      const price = p.props["sale price"] || 0;
      const cls = p.props["property class"];
      return (
        price >= priceRange.min &&
        price <= priceRange.max &&
        (classFilter === "All" || cls === classFilter)
      );
    });
    setSuggestions(filtered.slice(0, 8));
  }, [searchTerm, priceRange, classFilter]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const expr = [
      "all",
      [">=", ["get", "Sale Price"], priceRange.min],
      ["<=", ["get", "Sale Price"], priceRange.max],
    ];
    if (classFilter !== "All") {
      expr.push(["==", ["get", "Property Class"], classFilter]);
    }

    const applyFilter = () => {
      if (!map.getLayer("properties-layer")) return;
      
      try {
        map.setFilter("properties-layer", expr);
      } catch (error) {
        console.error("Error applying filter:", error);
      }
    };

    // Use a small delay to ensure layer is ready
    setTimeout(() => {
      if (map.isStyleLoaded() && map.getLayer("properties-layer")) {
        applyFilter();
      } else {
        const checkAndApply = () => {
          if (map.getLayer("properties-layer")) {
            applyFilter();
          }
        };
        map.once("styledata", checkAndApply);
        map.once("sourcedata", checkAndApply);
      }
    }, 100);
  }, [priceRange, classFilter, selectedCounty]);

  function showSelectedMarker(coords) {
    const map = mapRef.current;
    if (!map) return;

    // remove previous
    if (selectedMarkerRef.current) {
      selectedMarkerRef.current.remove();
      selectedMarkerRef.current = null;
    }

    // build new marker
    const el = document.createElement("div");
    el.className = "custom-marker-logo";
    el.style.cssText = `
      background-image:url('/realmaplogo.png');
      width:30px;height:30px;
      background-size:contain;background-repeat:no-repeat;
      border-radius:50%;
    `;

    selectedMarkerRef.current = new maplibregl.Marker(el)
      .setLngLat(coords)
      .addTo(map);
  }

  function selectSuggestion(p) {
    setSearchTerm(p.label);
    setSuggestions([]);
    setSelectedFeature(p);

    const map = mapRef.current;
    if (!map) return;

    map.flyTo({ center: p.coords, zoom: 16 });

    // Show custom logo marker (just like click on circle)
    showSelectedMarker(p.coords);

    // Optional: also update the invisible selected-property source (for internal use)
    if (map.getSource("selected-property")) {
      map.getSource("selected-property").setData({
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          geometry: { type: "Point", coordinates: p.coords },
        }],
      });
    }
  }

  function openNoteModal(property) {
    setPropertyToSave(property);
    setNoteInput("");
    setShowNoteModal(true);
  }

  async function submitSave() {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      alert("User not authenticated");
      return;
    }

    const { error } = await supabase.from("saved_properties").insert([
      { user_id: user.id, property_id: propertyToSave.id, notes: noteInput || "" },
    ]);

    if (error) {
      alert("Error saving property");
      console.error(error);
    } else {
      toast.success("Property saved!", { duration: 3000, position: "bottom-right" });
    }
    setShowNoteModal(false);
  }

  async function handleLogout() {
    await supabase.auth.signOut();
    window.location.reload();
  }

  window.saveProperty = (id) => {
    const match = propertyList.find((p) => p.id === id);
    if (match) openNoteModal(match);
  };

  // Helper function to format buyer name
  function formatBuyerName(buyerName) {
    if (!buyerName || buyerName === "<NA>" || buyerName === "N/A" || buyerName === "NULL") {
      return "Not Available";
    }
    return buyerName;
  }

  return (
    <div className="app-container">
      <div className="app-header">
        <header className="flex items-center justify-between px-6 py-4 bg-white shadow-md">
          <div className="flex items-center gap-3">
            <img src="/realmaplogo.png" alt="Logo" className="w-8 h-8" />
            <h1 className="text-2xl font-bold text-blue-700 font-lobster">Real Maps</h1>
          </div>
          <div className="flex items-center gap-4">
            <a href="/saved" className="text-blue-600 underline font-medium">💾 Saved</a>
            <button onClick={handleLogout} className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">Log Out</button>
          </div>
        </header>

        <div className="search-bar">
          <label>
            County:
            <select
              value={selectedCounty}
              onChange={(e) => setSelectedCounty(e.target.value)}
            >
              {Object.entries(counties).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>

          <div className="typeahead">
            <input
              type="text"
              placeholder="🔍 Search address…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setHighlightIndex(-1);
              }}
              onKeyDown={(e) => {
                if (!suggestions.length) return;

                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setHighlightIndex((i) =>
                    Math.min(i + 1, suggestions.length - 1)
                  );
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setHighlightIndex((i) => Math.max(i - 1, 0));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  const pick =
                    highlightIndex >= 0 ? suggestions[highlightIndex] : suggestions[0];
                  if (pick) {
                    selectSuggestion(pick);
                    setSuggestions([]);
                  }
                }
              }}
              onBlur={() => setTimeout(() => setSuggestions([]), 150)}
            />

            {suggestions.length > 0 && (
              <ul className="suggestions">
                {suggestions.map((p, i) => (
                  <li
                    key={p.id}
                    className={i === highlightIndex ? "active" : ""}
                    onMouseEnter={() => setHighlightIndex(i)}
                    onMouseDown={() => {
                      selectSuggestion(p);
                      setSuggestions([]);
                    }}
                  >
                    {p.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="filters">
            <label>
              Min $:
              <input
                type="number"
                value={priceRange.min}
                onChange={(e) =>
                  setPriceRange((pr) => ({ ...pr, min: +e.target.value }))
                }
              />
            </label>
            <label>
              Max $:
              <input
                type="number"
                value={priceRange.max}
                onChange={(e) =>
                  setPriceRange((pr) => ({ ...pr, max: +e.target.value }))
                }
              />
            </label>
            <label>
              Class:
              <select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
              >
                <option value="All">All</option>
                <option value="1">Vacant Land</option>
                <option value="2">Residential</option>
                <option value="4A">Commercial</option>
                <option value="4B">Industrial</option>
                <option value="4C">Apartments</option>
                <option value="15F">Other Exempt</option>
                <option value="15E">Cemeteries</option>
              </select>
            </label>
          </div>
        </div>
      </div>
        
      <div ref={mapContainer} style={{ flex: 1 }} />
      
      {selectedFeature && (
        <div className="property-fixed-popup shadow-xl rounded-lg bg-white w-full max-w-md fixed left-4 top-140 z-50 border border-gray-300">
          <div className="popup-header flex items-center justify-between">
            <h2 className="text-base font-semibold">
              {selectedFeature.props["property location"] || "Address Unknown"}
            </h2>
            <button
              onClick={() => setSelectedFeature(null)}
              className="close-popup-btn"
              aria-label="Close popup"
            >
              &times;
            </button>
          </div>

          <div className="popup-sub">
            {selectedFeature.props["city/state/zip"] || "City/Zip Missing"}
          </div>

          <div className="popup-body text-sm">
            {Object.entries(selectedFeature.props)
              .filter(([key, value]) => {
                // Skip location fields already in header AND empty/meaningless fields
                const skipFields = [
                  "property location", "city/state/zip", "latitude", "longitude", "seller name"
                ];
                
                const isEmpty = !key || key.trim() === '' || key === 'undefined' || key === 'null';
                const isSkipped = skipFields.includes(key.toLowerCase());
                const isInvalidKey = !key || /^\s*$/.test(key) || /^\d+$/.test(key.trim());
                const isEmptyStringKey = key === '';
                
                return !isEmpty && !isSkipped && !isInvalidKey && !isEmptyStringKey;
              })
              .sort(([a], [b]) => {
                // Put important fields first
                const priority = {
                  'property class': 1,
                  'municipality': 2, 
                  'zone': 3,
                  'building class': 4,
                  'total assmnt': 5,
                  'land assmnt': 6,
                  'building assmnt': 7,
                  'sale price': 8,
                  'sale date': 9,
                  'buyer name': 10,
                  'land desc': 11,
                  'building desc': 12,
                  'stydesc': 13,
                  'nu code': 14,
                  'block': 15,
                  'lot': 16
                };
                return (priority[a] || 999) - (priority[b] || 999);
              })
              .map(([key, value]) => {
                // Format field names nicely
                let fieldName = key || 'Unknown Field';
                if (fieldName.trim()) {
                  fieldName = fieldName.split(' ').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ').replace('Stydesc', 'Style').replace('Nu Code', 'NU Code').replace('Assmnt', 'Assessment');
                }
                
                // Format values based on field type
                let formattedValue = value;
                if (value === null) {
                  formattedValue = "NULL";
                } else if (key === 'buyer name') {
                  formattedValue = formatBuyerName(value);
                } else if (typeof value === 'number') {
                  if (key.includes('price') || key.includes('assmnt') || key.includes('taxes')) {
                    formattedValue = `${value.toLocaleString()}`;
                  }
                } else if (typeof value === 'string' && value.includes('T00:00:00')) {
                  formattedValue = new Date(value).toLocaleDateString();
                }
                
                return (
                  <p key={key}>
                    <strong>{fieldName}:</strong> {String(formattedValue)}
                  </p>
                );
              })
            }

            {/* Calculated fields */}
            {selectedFeature.props["sale price"] && selectedFeature.props["acreage"] && selectedFeature.props["acreage"] > 0.01 && (
              <p><strong>$/Acre:</strong> ${Math.round(selectedFeature.props["sale price"] / selectedFeature.props["acreage"]).toLocaleString()}</p>
            )}
            
            {selectedFeature.props["sale price"] && selectedFeature.props["sq. ft."] && selectedFeature.props["sq. ft."] > 0 && (
              <p><strong>$/Sq Ft:</strong> ${(selectedFeature.props["sale price"] / selectedFeature.props["sq. ft."]).toFixed(2)}</p>
            )}

            <p className="popup-link mt-2">
              <a href="https://oprs.co.monmouth.nj.us/oprs/googlewithuc/default.aspx" target="_blank" rel="noopener noreferrer">
                🔍 Search Tax Map
              </a>
            </p>

            <div className="flex justify-end mt-3">
              <button
                onClick={() => openNoteModal(selectedFeature)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded text-sm"
              >
                💾 Save Property
              </button>
            </div>
          </div>
        </div>
      )}

      {showNoteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Add a note for this property</h3>
            <textarea
              rows="4"
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              placeholder="Write your note here..."
            />
            <div className="modal-buttons">
              <button onClick={() => setShowNoteModal(false)}>Cancel</button>
              <button onClick={submitSave}>Save</button>
            </div>
          </div>
        </div>
      )}
      <Toaster />
    </div>
  );
}